import { FormsyRadioGroup, Radio } from '@thanx/uikit/radio'
import { FormsySelect } from '@thanx/uikit/select'
import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useGetSegmentQuery, useGetTargetSegmentsQuery } from 'api/segments'
import { useGetUsersQuery } from 'api/users'
import HelpLink from 'components/HelpLink'
import Section from 'components/Section'
import Spinner from 'components/Spinner'
import StatusBadge from 'components/StatusBadge'
import { buildTranslate } from 'locales'
import type { Fields as TargetSegment } from 'models/TargetSegment'
import { useMemo, useRef } from 'react'
import { FormGroup, FormLabel } from 'react-bootstrap-five'
import { useSelector } from 'react-redux'
import { I18n } from 'react-redux-i18n'

type Props = {
  blockId: string
  audienceType: string
  selectedSegmentId: number | undefined
  updateContentBlock: (blockId: string, data: any) => void
}

const t = buildTranslate(
  'cms.content.app_home_page.content.edit_block.configuration'
)

const AudienceSection = ({
  blockId,
  audienceType,
  selectedSegmentId,
  updateContentBlock,
}: Props) => {
  const currentMerchantId = useSelector(
    (state: any) => state.auth.current_merchant_id
  )
  const { data: segmentsResponse } = useGetTargetSegmentsQuery()

  // Fetch users data to get total_membership_count - matches "all customers" page implementation
  const { data: usersResponse, isLoading: isLoadingUsers } = useGetUsersQuery({
    page: 1,
  })

  // Fetch selected segment details with RTK Query (only when segment is selected)
  const { data: segmentResponse, isFetching: isSegmentFetching } =
    useGetSegmentQuery(
      { segmentId: selectedSegmentId?.toString() || '' },
      {
        skip:
          selectedSegmentId === undefined || audienceType !== 'select_segment',
      }
    )

  // Use isFetching instead of manual state - it handles both initial loads and refetches
  const isLoadingSegmentData = isSegmentFetching

  // Filter segments for current merchant
  const targetSegments: TargetSegment[] = useMemo(() => {
    if (!segmentsResponse?.target_segments || !currentMerchantId) return []
    return segmentsResponse.target_segments.filter(
      segment => segment.merchant_id === currentMerchantId
    )
  }, [segmentsResponse, currentMerchantId])

  const selectRef = useRef<any>(null)

  const segmentOptions = useMemo(() => {
    return targetSegments.map(segment => ({
      value: segment.id,
      label: segment.name,
    }))
  }, [targetSegments])

  const selectedSegment = useMemo(() => {
    if (audienceType !== 'select_segment' || !selectedSegmentId) {
      return undefined
    }

    return (
      segmentResponse?.target_segment ||
      targetSegments.find(segment => segment.id === selectedSegmentId)
    )
  }, [segmentResponse, targetSegments, selectedSegmentId, audienceType])

  // Get customer count from users API - match "all customers" page implementation
  const totalCustomerCount = usersResponse?.total_membership_count || 0

  // Custom keydown handler for backspace clearing
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Backspace' && selectedSegmentId) {
      // Check if input is empty (no filter text)
      const input = selectRef.current?.querySelector('input')
      if (input && input.value === '') {
        event.preventDefault()
        updateContentBlock(blockId, { segment_id: null })
      }
    }
  }

  return (
    <Section className="flex-1 mb-l mr-xs p-l">
      <div className="d-flex align-items-center mb-xs">
        <Text.Header4 className="mb-0">{t('audience.title')}</Text.Header4>
        <div style={{ marginTop: '8px' }}>
          <StatusBadge type="beta" />
        </div>
      </div>
      <Text.BodyText4 color="grey70" className="mb-l">
        {t('audience.description')}
      </Text.BodyText4>

      <div className="mt-m">
        <FormLabel className="body-text-4 bold mb-s">
          {t('audience.label')}
        </FormLabel>
        <FormsyRadioGroup
          name={`${blockId}.audience_type`}
          value={audienceType}
          align="vertical"
          className="mb-l"
        >
          <Radio value="all_customers" className="mb-xs">
            <div className="d-flex flex-column">
              <Text.BodyText3 className="mb-xs">
                {t('audience.all_customers.title')}
              </Text.BodyText3>
              <Text.BodyText4 color="grey70">
                <Spinner isLoading={isLoadingUsers} className="inline-block">
                  {t('audience.all_customers.description', {
                    count: totalCustomerCount.toLocaleString(),
                  })}
                </Spinner>
              </Text.BodyText4>
            </div>
          </Radio>
          <Radio value="select_segment">
            <div className="d-flex flex-column">
              <Text.BodyText3 className="mb-xs">
                {t('audience.select_segment.title')}
              </Text.BodyText3>
              <Text.BodyText4 color="grey70">
                {t('audience.select_segment.description')}
              </Text.BodyText4>
            </div>
          </Radio>
        </FormsyRadioGroup>
      </div>

      <Switch condition={audienceType === 'select_segment'}>
        <>
          <FormGroup className="mb-l">
            <FormLabel className="body-text-4 bold mb-xs">
              {t('audience.segments.label')}
            </FormLabel>
            <div ref={selectRef} onKeyDown={handleKeyDown}>
              <FormsySelect
                name={`${blockId}.segment_id`}
                options={segmentOptions}
                placeholder={t('audience.segments.placeholder')}
                zIndex={1002}
                maxDropdownHeight="200px"
                value={selectedSegmentId}
                onChange={value => {
                  updateContentBlock(blockId, { segment_id: value })
                }}
                validations={
                  audienceType === 'select_segment' ? { isRequired: true } : {}
                }
                validationError={I18n.t('validations.is_required')}
              />
            </div>
            <Switch
              condition={
                audienceType === 'select_segment' &&
                (Boolean(selectedSegment) || isLoadingSegmentData)
              }
            >
              <Switch condition={isLoadingSegmentData}>
                <Spinner isLoading className="inline-block mt-xs" />
              </Switch>
              <Switch
                condition={
                  !isLoadingSegmentData &&
                  Boolean(selectedSegment) &&
                  audienceType === 'select_segment'
                }
              >
                <div>
                  <Text.BodyText4 color="grey70" className="mt-xs">
                    {t('audience.segments.customer_count', {
                      count: selectedSegment?.count?.toLocaleString() || '0',
                    })}
                  </Text.BodyText4>

                  <Switch
                    condition={Boolean(
                      selectedSegment && selectedSegment.description
                    )}
                  >
                    <FormGroup className="mb-l mt-l">
                      <FormLabel className="body-text-4 bold mb-xs d-block">
                        {t('audience.segments.description_label')}
                      </FormLabel>
                      <Text.BodyText3 color="grey70" className="mt-xs">
                        {selectedSegment?.description}
                      </Text.BodyText3>
                    </FormGroup>
                  </Switch>
                </div>
              </Switch>
            </Switch>
          </FormGroup>
        </>
      </Switch>

      <div className="mt-l">
        <HelpLink url="https://help.thanx.com/hc/en-us/articles/36983830301975-Personalized-home-pages-Segmentable-and-schedulable-content-blocks">
          Learn more about segmentable blocks and explore ideas
        </HelpLink>
      </div>
    </Section>
  )
}

export default AudienceSection
